// Helpers for the "Import guests" feature: read a contacts CSV / vCard file, turn phone-picker
// results into guests, and clean phone numbers. Everything here is plain functions (no React).
//
// A "guest row" looks like: { firstName, lastName, phone, email, companions }

const DEFAULT_COUNTRY_CODE = "966"; // Saudi Arabia

// Countries offered in the import screen (and understood in a "Country" column of a sheet).
// Names/aliases are lower case; the sheet may use the dial code (+971), a name, or a 2-letter code.
export const COUNTRIES = [
  { code: "966", iso: "sa", en: "Saudi Arabia", ar: "السعودية", names: ["ksa", "saudi", "المملكة العربية السعودية"] },
  { code: "971", iso: "ae", en: "United Arab Emirates", ar: "الإمارات", names: ["uae", "emirates", "الامارات", "الإمارات العربية المتحدة"] },
  { code: "965", iso: "kw", en: "Kuwait", ar: "الكويت", names: [] },
  { code: "974", iso: "qa", en: "Qatar", ar: "قطر", names: [] },
  { code: "973", iso: "bh", en: "Bahrain", ar: "البحرين", names: [] },
  { code: "968", iso: "om", en: "Oman", ar: "عُمان", names: ["عمان"] },
  { code: "20", iso: "eg", en: "Egypt", ar: "مصر", names: [] },
  { code: "962", iso: "jo", en: "Jordan", ar: "الأردن", names: ["الاردن"] },
  { code: "961", iso: "lb", en: "Lebanon", ar: "لبنان", names: [] },
  { code: "963", iso: "sy", en: "Syria", ar: "سوريا", names: ["سورية"] },
  { code: "964", iso: "iq", en: "Iraq", ar: "العراق", names: [] },
  { code: "967", iso: "ye", en: "Yemen", ar: "اليمن", names: [] },
  { code: "249", iso: "sd", en: "Sudan", ar: "السودان", names: [] },
  { code: "212", iso: "ma", en: "Morocco", ar: "المغرب", names: [] },
  { code: "91", iso: "in", en: "India", ar: "الهند", names: [] },
  { code: "92", iso: "pk", en: "Pakistan", ar: "باكستان", names: [] },
  { code: "880", iso: "bd", en: "Bangladesh", ar: "بنغلاديش", names: [] },
  { code: "63", iso: "ph", en: "Philippines", ar: "الفلبين", names: [] },
  { code: "90", iso: "tr", en: "Turkey", ar: "تركيا", names: ["turkiye"] },
  { code: "44", iso: "gb", en: "United Kingdom", ar: "المملكة المتحدة", names: ["uk", "england", "britain"] },
  { code: "1", iso: "us", en: "United States", ar: "الولايات المتحدة", names: ["usa", "america", "canada"] },
];

// ---------- phone numbers (same rules as the server's PhoneNormalizer) ----------

// Arabic-Indic (٠-٩) and Persian (۰-۹) digits -> 0-9, so every spelling of a number compares equal.
const toWesternDigits = (value) =>
  String(value || "")
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

// Returns "+<country code><number>" or null when it cannot be a real number.
export const normalizePhone = (raw, defaultCode = DEFAULT_COUNTRY_CODE) => phoneCheck(raw, defaultCode).phone;

// Mobile rules for the countries we know (same as the server): digits after the country code, and
// the digit a mobile starts with. A truncated number can never get WhatsApp messages. Other
// countries only get the 8-15 digit check.
const MOBILE_RULES = {
  966: { length: 9, start: "5" }, // Saudi Arabia
  971: { length: 9 }, // UAE
  965: { length: 8 }, // Kuwait
  974: { length: 8 }, // Qatar
  973: { length: 8 }, // Bahrain
  968: { length: 8 }, // Oman
};

// Returns { phone } for a usable number, or { phone: null, error, found, expected } where error is one of
// phone_missing, phone_invalid, phone_too_short, phone_too_long, phone_not_mobile.
export const phoneCheck = (raw, defaultCode = DEFAULT_COUNTRY_CODE) => {
  if (!String(raw || "").trim()) return { phone: null, error: "phone_missing" };
  const result = normalizePhoneLoose(raw, defaultCode);
  if (!result) return { phone: null, error: "phone_invalid" };
  const code = Object.keys(MOBILE_RULES).find((c) => result.startsWith(`+${c}`));
  if (code) {
    const rule = MOBILE_RULES[code];
    const local = result.slice(code.length + 1);
    if (local.length < rule.length) return { phone: null, error: "phone_too_short", found: local.length, expected: rule.length };
    if (local.length > rule.length) return { phone: null, error: "phone_too_long", found: local.length, expected: rule.length };
    if (rule.start && local[0] !== rule.start) return { phone: null, error: "phone_not_mobile", found: local.length, expected: rule.length };
  }
  return { phone: result };
};

const normalizePhoneLoose = (raw, defaultCode) => {
  if (!raw) return null;
  const trimmed = toWesternDigits(raw).trim();
  let hasPlus = trimmed.startsWith("+");
  let digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;

  if (!hasPlus && digits.startsWith("00")) {
    hasPlus = true;
    digits = digits.slice(2);
  }
  if (hasPlus) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;

  const local = digits.replace(/^0+/, "");
  if (!local) return null;
  if (local.length <= 9) return local.length >= 7 ? `+${defaultCode}${local}` : null;
  // A single leading 0 is the domestic way of writing a number (05...), so it belongs to the default
  // country even when it has too many digits; that way the extra digit is reported instead of guessed.
  if (/^0[^0]/.test(digits)) return local.length <= 12 ? `+${defaultCode}${local}` : null;
  return local.length <= 15 ? `+${local}` : null;
};

// Full number as digits (country code + number): the same person whatever way it was written,
// while the same ending in two different countries stays two different numbers.
// Expects a number already cleaned by normalizePhone.
export const phoneMatchKey = (phone) => toWesternDigits(phone).replace(/\D/g, "");

// Reads a "country" cell: a dial code (+971, 00971, 971) or a country name / 2-letter code.
// Returns the dial code digits (e.g. "971") or "" when it is not understood.
export const parseCountryCell = (raw) => {
  const text = toWesternDigits(raw).trim().toLowerCase();
  if (!text) return "";
  const asDigits = text.replace(/^\+/, "").replace(/^00/, "").replace(/[\s-]/g, "");
  if (/^\d{1,4}$/.test(asDigits)) return asDigits;
  const match = COUNTRIES.find(
    (c) => c.iso === text || c.en.toLowerCase() === text || c.ar === text || c.names.includes(text)
  );
  return match ? match.code : "";
};

// Puts a row's own country code in front of its number. A number that already carries its code
// (a + or 00 prefix, or the code already written in front) is left as it is.
export const applyCountryCode = (phone, countryCode) => {
  if (!phone || !countryCode) return phone;
  const trimmed = toWesternDigits(phone).trim();
  if (trimmed.startsWith("+") || trimmed.replace(/\D/g, "").startsWith("00")) return phone;
  const digits = trimmed.replace(/\D/g, "").replace(/^0+/, "");
  if (!digits) return phone;
  if (digits.startsWith(countryCode) && digits.length >= countryCode.length + 7) return "+" + digits;
  return "+" + countryCode + digits;
};

// ---------- names ----------

const splitFullName = (full) => {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

// ---------- CSV ----------

// Small CSV reader: handles quotes, commas inside quotes, line breaks inside quotes, and
// comma / semicolon / tab separated files (Excel in some regions saves with semicolons).
export const parseCsvText = (input) => {
  const text = String(input || "").replace(/^﻿/, "");
  const firstLine = text.split(/\r?\n/, 1)[0] || "";
  const count = (ch) => (firstLine.match(new RegExp(`\\${ch}`, "g")) || []).length;
  const delimiter = [",", ";", "\t"].reduce((best, d) => (count(d) > count(best) ? d : best), ",");

  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else {
      cell += ch;
    }
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
};

const norm = (s) => String(s || "").trim().toLowerCase();

// Picks which column holds what, from the header names. Handles our own template, Google
// Contacts ("First Name", "Phone 1 - Value", "E-mail 1 - Value") and iCloud/Outlook style
// ("Mobile Phone", "Given Name") exports.
const detectColumns = (headers) => {
  const h = headers.map(norm);
  const indexesWhere = (test) => h.map((name, i) => (test(name) ? i : -1)).filter((i) => i >= 0);

  const first = indexesWhere((n) => /^(first name|given name|firstname|first|الاسم الأول)$/.test(n))[0];
  const last = indexesWhere((n) => /^(last name|family name|surname|lastname|last|اسم العائلة)$/.test(n))[0];
  const full = indexesWhere((n) => /^(name|full name|display name|fullname|الاسم)$/.test(n))[0];

  // Optional column with each guest's country (+971, 971, UAE ...). Never mistaken for the phone column.
  const countryCol = indexesWhere((n) => /country|dial|calling|الدولة|دولة|رمز/.test(n))[0];
  const phoneCols = indexesWhere((n, i) => /phone|mobile|cell|tel\b|whatsapp|جوال|هاتف|موبايل|رقم/.test(n) && !/type|label/.test(n) && i !== countryCol);
  // Mobile columns first, because that is where WhatsApp numbers live.
  phoneCols.sort((a, b) => Number(/mobile|cell|whatsapp|جوال|موبايل/.test(h[b])) - Number(/mobile|cell|whatsapp|جوال|موبايل/.test(h[a])));

  const email = indexesWhere((n) => /e-?mail|بريد/.test(n) && !/type|label/.test(n))[0];
  const companions = indexesWhere((n) => /companion|plus.?one|guests?\b|مرافق/.test(n))[0];

  return { first, last, full, phoneCols, email, companions, countryCol };
};

export const guestsFromCsv = (text) => guestsFromRows(parseCsvText(text));

// Turns a table of cells (first row = headers) into guest rows. Shared by CSV and Excel files.
export const guestsFromRows = (rows) => {
  if (rows.length < 2) return [];
  const cols = detectColumns(rows[0]);
  const get = (row, i) => (i == null ? "" : String(row[i] || "").trim());

  return rows.slice(1).map((row) => {
    let firstName = get(row, cols.first);
    let lastName = get(row, cols.last);
    if (!firstName && !lastName) ({ firstName, lastName } = splitFullName(get(row, cols.full)));

    // Google puts several numbers in one cell separated by " ::: "; take the first usable one.
    let phone = "";
    for (const i of cols.phoneCols) {
      const candidate = get(row, i).split(":::")[0].trim();
      if (candidate) {
        phone = candidate;
        break;
      }
    }

    // An optional "Country Code" column (+971, 971, UAE, ...) is added in front of that row's number.
    phone = applyCountryCode(phone, parseCountryCell(get(row, cols.countryCol)));

    return {
      firstName,
      lastName,
      phone,
      email: get(row, cols.email).split(":::")[0].trim(),
      companions: Math.max(0, parseInt(get(row, cols.companions), 10) || 0),
    };
  });
};

// ---------- vCard (.vcf) ----------

// Older Android exports write Arabic names as quoted-printable ("=D8=A3=D8=AD..."); turn that back into text.
const decodeQuotedPrintable = (value) => {
  const bytes = [];
  for (let i = 0; i < value.length; i += 1) {
    if (value[i] === "=" && /^[0-9A-Fa-f]{2}$/.test(value.slice(i + 1, i + 3))) {
      bytes.push(parseInt(value.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      bytes.push(value.charCodeAt(i) & 0xff);
    }
  }
  try {
    return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
  } catch (e) {
    return value;
  }
};

const unescapeVcard = (value) => value.replace(/\\n/gi, " ").replace(/\\([,;\\])/g, "$1");

export const guestsFromVcf = (text) => {
  // Join folded lines: a line starting with a space/tab continues the one before it, and a
  // quoted-printable line ending in "=" continues on the next line.
  const unfolded = String(text || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/=\n/g, "")
    .replace(/\n[ \t]/g, "");

  const guests = [];
  let card = null;

  unfolded.split("\n").forEach((line) => {
    const upper = line.trim().toUpperCase();
    if (upper === "BEGIN:VCARD") {
      card = { n: "", fn: "", tels: [], email: "" };
      return;
    }
    if (upper === "END:VCARD") {
      if (card) {
        let firstName = "";
        let lastName = "";
        if (card.n) {
          const [family = "", given = "", additional = ""] = card.n.split(";");
          firstName = [given, additional].filter(Boolean).join(" ").trim();
          lastName = family.trim();
        }
        if (!firstName && !lastName) ({ firstName, lastName } = splitFullName(card.fn));

        // Prefer a mobile number over home/work numbers.
        const best = card.tels.find((t) => /cell|mobile|iphone/i.test(t.params)) || card.tels[0];
        guests.push({ firstName, lastName, phone: best ? best.value : "", email: card.email, companions: 0 });
      }
      card = null;
      return;
    }
    if (!card) return;

    const colon = line.indexOf(":");
    if (colon < 0) return;
    const rawKey = line.slice(0, colon);
    let value = line.slice(colon + 1);
    // Drop group prefixes like "item1.TEL".
    const [nameWithGroup, ...paramParts] = rawKey.split(";");
    const name = nameWithGroup.split(".").pop().toUpperCase();
    const params = paramParts.join(";");
    if (/ENCODING=QUOTED-PRINTABLE/i.test(params)) value = decodeQuotedPrintable(value);
    value = unescapeVcard(value).trim();

    if (name === "N") card.n = value;
    else if (name === "FN") card.fn = value;
    else if (name === "TEL") card.tels.push({ value, params });
    else if (name === "EMAIL" && !card.email) card.email = value;
  });

  return guests;
};

// ---------- phone contact picker (Android Chrome) ----------

export const guestsFromContactPicker = (contacts) =>
  (contacts || []).map((contact) => {
    const { firstName, lastName } = splitFullName((contact.name && contact.name[0]) || "");
    return {
      firstName,
      lastName,
      phone: (contact.tel && contact.tel[0]) || "",
      email: (contact.email && contact.email[0]) || "",
      companions: 0,
    };
  });

// ---------- template ----------

// A small CSV the host can fill in a spreadsheet. The BOM makes Excel read Arabic names correctly.
export const downloadGuestTemplate = (withEmail) => {
  // "Country Code" is optional: leave it empty and the country chosen on the import screen is used.
  // A number written with its own + code (the last example) needs no country at all.
  const header = withEmail
    ? "First Name,Last Name,Country Code,Phone,Email,Companions"
    : "First Name,Last Name,Country Code,Phone,Companions";
  const examples = [
    ["Ahmed", "Ali", "+966", "0501234567", "ahmed@example.com", "2"],
    ["Sara", "Khan", "+971", "0559876543", "sara@example.com", "0"],
    ["Ravi", "Kumar", "+91", "9446743922", "ravi@example.com", "1"],
    ["Noor", "Hassan", "", "+201001234567", "noor@example.com", "0"],
  ].map((r) => (withEmail ? r : [r[0], r[1], r[2], r[3], r[5]]).join(","));
  const blob = new Blob(["﻿" + [header, ...examples].join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "guests-template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ---------- Excel (.xlsx) ----------

// An .xlsx file is a zip of XML files. Browsers can unzip ("inflate") by themselves, so no
// library is needed: find the sheet and the shared-strings list inside the zip, then read the cells.

const inflateRaw = async (bytes) => {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Response(stream).arrayBuffer();
};

const readZipEntries = async (buffer) => {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // The "end of central directory" record sits at the very end of the file.
  let end = -1;
  for (let i = buffer.byteLength - 22; i >= Math.max(0, buffer.byteLength - 65557); i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      end = i;
      break;
    }
  }
  if (end < 0) throw new Error("Not a zip file");

  const count = view.getUint16(end + 10, true);
  let pos = view.getUint32(end + 16, true);
  const entries = {};

  for (let n = 0; n < count; n += 1) {
    if (view.getUint32(pos, true) !== 0x02014b50) break;
    const method = view.getUint16(pos + 10, true);
    const compressedSize = view.getUint32(pos + 20, true);
    const nameLength = view.getUint16(pos + 28, true);
    const extraLength = view.getUint16(pos + 30, true);
    const commentLength = view.getUint16(pos + 32, true);
    const localOffset = view.getUint32(pos + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(pos + 46, pos + 46 + nameLength));
    pos += 46 + nameLength + extraLength + commentLength;

    entries[name] = async () => {
      const localNameLength = view.getUint16(localOffset + 26, true);
      const localExtraLength = view.getUint16(localOffset + 28, true);
      const start = localOffset + 30 + localNameLength + localExtraLength;
      const data = bytes.subarray(start, start + compressedSize);
      const raw = method === 0 ? data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) : await inflateRaw(data);
      return new TextDecoder("utf-8").decode(raw);
    };
  }
  return entries;
};

const columnIndex = (cellRef) => {
  const letters = (cellRef.match(/^[A-Z]+/i) || ["A"])[0].toUpperCase();
  return letters.split("").reduce((total, ch) => total * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
};

// Excel may save a long phone number in scientific form (e.g. 5.01234567E8); turn it back into digits.
const readableNumber = (value) => (/e/i.test(value) && !Number.isNaN(Number(value)) ? Number(value).toFixed(0) : value);

export const readXlsxRows = async (buffer) => {
  const entries = await readZipEntries(buffer);
  const parser = new DOMParser();
  const xml = async (name) => parser.parseFromString(await entries[name](), "application/xml");

  const sharedStrings = [];
  if (entries["xl/sharedStrings.xml"]) {
    const doc = await xml("xl/sharedStrings.xml");
    doc.querySelectorAll("si").forEach((si) => {
      sharedStrings.push([...si.querySelectorAll("t")].map((t) => t.textContent).join(""));
    });
  }

  const sheetName = Object.keys(entries)
    .filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n))
    .sort((a, b) => parseInt(a.match(/\d+/)[0], 10) - parseInt(b.match(/\d+/)[0], 10))[0];
  if (!sheetName) throw new Error("No sheet found");

  const sheet = await xml(sheetName);
  const rows = [];
  sheet.querySelectorAll("sheetData > row").forEach((rowEl) => {
    const row = [];
    rowEl.querySelectorAll("c").forEach((cell) => {
      const type = cell.getAttribute("t");
      const v = cell.querySelector("v")?.textContent ?? "";
      let value = v;
      if (type === "s") value = sharedStrings[parseInt(v, 10)] ?? "";
      else if (type === "inlineStr") value = [...cell.querySelectorAll("t")].map((t) => t.textContent).join("");
      else value = readableNumber(v);
      row[columnIndex(cell.getAttribute("r") || "A1")] = String(value).trim();
    });
    for (let i = 0; i < row.length; i += 1) if (row[i] === undefined) row[i] = "";
    rows.push(row);
  });
  return rows.filter((r) => r.some((c) => c !== ""));
};

// Reads a chosen file and returns guest rows. CSV, Excel (.xlsx) and vCard.
export const readGuestFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const name = (file.name || "").toLowerCase();

  // "PK" at the start means a zip, which is what an .xlsx file is.
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
    if (name.endsWith(".xls") && !name.endsWith(".xlsx")) throw new Error("Old .xls files are not supported");
    return guestsFromRows(await readXlsxRows(buffer));
  }

  const text = new TextDecoder("utf-8").decode(buffer);
  if (name.endsWith(".vcf") || /BEGIN:VCARD/i.test(text.slice(0, 200))) return guestsFromVcf(text);
  return guestsFromCsv(text);
};

// ---------- rows that need attention ----------

// Downloads rows (with a "reason" each) as a CSV in the same layout as the template, so the host can
// send it back to whoever supplied the list, or fix it and import it again.
export const downloadRowsCsv = (list, withEmail) => {
  const cell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const header = withEmail
    ? ["First Name", "Last Name", "Phone", "Email", "Companions", "Reason"]
    : ["First Name", "Last Name", "Phone", "Companions", "Reason"];
  const lines = list.map((r) =>
    (withEmail
      ? [r.firstName, r.lastName, r.phone, r.email, r.companions, r.reason]
      : [r.firstName, r.lastName, r.phone, r.companions, r.reason]
    )
      .map(cell)
      .join(",")
  );
  const blob = new Blob(["﻿" + [header.map(cell).join(","), ...lines].join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "guests-needing-attention.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
